import { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { 
  MapPin, Plus, Calendar, Clock, Bus, Users, Search, Loader2, UserCheck, ShieldCheck,
  XCircle, AlertTriangle, FileText
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULT_DRIVERS = [
  { id: 'driver-takudzwa', full_name: 'Takudzwa Kuwenyi (Driver)', phone: '+263 777 275 154' },
  { id: 'driver-tafadzwa', full_name: 'Tafadzwa Moyo (Driver)', phone: '+263 771 234 567' },
  { id: 'driver-farai', full_name: 'Farai Dube (Driver)', phone: '+263 772 345 678' }
];

const DEFAULT_CONDUCTORS = [
  { id: 'conductor-chipo', full_name: 'Chipo Chiduku (Host)', phone: '+263 786 563 430' },
  { id: 'conductor-ruvimbo', full_name: 'Ruvimbo Marufu (Host)', phone: '+263 781 234 567' }
];

// Memoized form component to prevent re-renders causing input lag
const TripFormFields = memo(({ formData, onChange, routes, buses, drivers = [], conductors = [] }) => {
  const handleChange = useCallback((field, value) => {
    onChange(field, value);
  }, [onChange]);

  const activeDrivers = drivers.length > 0 ? drivers : DEFAULT_DRIVERS;
  const activeConductors = conductors.length > 0 ? conductors : DEFAULT_CONDUCTORS;

  const handleDriverSelect = (id) => {
    const found = activeDrivers.find(d => d.id === id);
    if (found) {
      handleChange('driver_id', found.id);
      handleChange('driver_name', found.full_name);
      handleChange('driver_phone', found.phone || '+263 777 275 154');
    } else {
      handleChange('driver_id', id);
    }
  };

  const handleConductorSelect = (id) => {
    const found = activeConductors.find(c => c.id === id);
    if (found) {
      handleChange('conductor_id', found.id);
      handleChange('conductor_name', found.full_name);
      handleChange('conductor_phone', found.phone || '+263 786 563 430');
    } else {
      handleChange('conductor_id', id);
    }
  };

  return (
    <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
      <div>
        <Label>Route</Label>
        <Select 
          defaultValue={formData.route_id}
          onValueChange={(val) => handleChange('route_id', val)}
        >
          <SelectTrigger data-testid="trip-route-select">
            <SelectValue placeholder="Select route" />
          </SelectTrigger>
          <SelectContent>
            {routes.map(route => (
              <SelectItem key={route.id} value={route.id}>
                {route.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Bus</Label>
        <Select 
          defaultValue={formData.bus_id}
          onValueChange={(val) => handleChange('bus_id', val)}
        >
          <SelectTrigger data-testid="trip-bus-select">
            <SelectValue placeholder="Select bus" />
          </SelectTrigger>
          <SelectContent>
            {buses.map(bus => (
              <SelectItem key={bus.id} value={bus.id}>
                {bus.registration_number} - {bus.model} ({bus.seat_layout || '3-2'})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Trip Crew: Driver & Host / Conductor */}
      <div className="p-3 bg-blue-50/60 border border-blue-200 rounded-lg space-y-3">
        <p className="text-xs font-bold text-blue-900 uppercase tracking-wider flex items-center gap-1.5">
          <UserCheck className="h-4 w-4 text-blue-700" />
          Trip Crew Assignment (Required)
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-blue-900 font-semibold">Assigned Driver *</Label>
            <Select 
              defaultValue={formData.driver_id || activeDrivers[0]?.id}
              onValueChange={handleDriverSelect}
            >
              <SelectTrigger data-testid="trip-driver-select" className="bg-white">
                <SelectValue placeholder="Select Driver" />
              </SelectTrigger>
              <SelectContent>
                {activeDrivers.map(d => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              className="mt-1.5 text-xs bg-white"
              placeholder="Driver Name (if custom)"
              value={formData.driver_name || ''}
              onChange={(e) => handleChange('driver_name', e.target.value)}
            />
          </div>

          <div>
            <Label className="text-xs text-blue-900 font-semibold">Assigned Host *</Label>
            <Select 
              defaultValue={formData.conductor_id || activeConductors[0]?.id}
              onValueChange={handleConductorSelect}
            >
              <SelectTrigger data-testid="trip-conductor-select" className="bg-white">
                <SelectValue placeholder="Select Host" />
              </SelectTrigger>
              <SelectContent>
                {activeConductors.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              className="mt-1.5 text-xs bg-white"
              placeholder="Host Name (if custom)"
              value={formData.conductor_name || ''}
              onChange={(e) => handleChange('conductor_name', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Departure</Label>
          <Input
            type="datetime-local"
            defaultValue={formData.departure_datetime}
            onBlur={(e) => handleChange('departure_datetime', e.target.value)}
            data-testid="trip-departure-input"
          />
        </div>
        <div>
          <Label>Arrival</Label>
          <Input
            type="datetime-local"
            defaultValue={formData.arrival_datetime}
            onBlur={(e) => handleChange('arrival_datetime', e.target.value)}
            data-testid="trip-arrival-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Price (USD)</Label>
          <Input
            type="number"
            defaultValue={formData.price_usd}
            onBlur={(e) => handleChange('price_usd', Number(e.target.value))}
            data-testid="trip-price-usd-input"
          />
        </div>
        <div>
          <Label>Price (ZAR)</Label>
          <Input
            type="number"
            defaultValue={formData.price_zar}
            onBlur={(e) => handleChange('price_zar', Number(e.target.value))}
            data-testid="trip-price-zar-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Trip Expense Float / Allowance</Label>
          <Input
            type="number"
            step="0.01"
            defaultValue={formData.expense_allowance || 0}
            onBlur={(e) => handleChange('expense_allowance', Number(e.target.value))}
            data-testid="trip-expense-allowance-input"
            placeholder="e.g. 150.00"
          />
          <p className="text-[11px] text-slate-500 mt-0.5">Budget given to crew for fuel, tolls, etc.</p>
        </div>
        <div>
          <Label>Float Currency</Label>
          <Select
            defaultValue={formData.expense_currency || 'USD'}
            onValueChange={(val) => handleChange('expense_currency', val)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">USD ($)</SelectItem>
              <SelectItem value="ZAR">ZAR (R)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
});

TripFormFields.displayName = 'TripFormFields';

const AdminTrips = () => {
  const navigate = useNavigate();
  const { user, token, isOverseer, hasDuty } = useAuth();
  const [trips, setTrips] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [buses, setBuses] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [conductors, setConductors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);

  // Cancel dialog state
  const [cancelModalTrip, setCancelModalTrip] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    route_id: '',
    bus_id: '',
    driver_id: 'driver-takudzwa',
    driver_name: 'Takudzwa Kuwenyi (Driver)',
    driver_phone: '+263 777 275 154',
    conductor_id: 'conductor-chipo',
    conductor_name: 'Chipo Chiduku (Host)',
    conductor_phone: '+263 786 563 430',
    departure_datetime: '',
    arrival_datetime: '',
    price_usd: 0,
    price_zar: 0,
    expense_allowance: 0,
    expense_currency: 'USD'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tripsRes, routesRes, busesRes] = await Promise.all([
        axios.get(`${API_URL}/trips`),
        axios.get(`${API_URL}/routes`),
        axios.get(`${API_URL}/buses`)
      ]);
      setTrips(tripsRes.data);
      setRoutes(routesRes.data);
      setBuses(busesRes.data);

      // Fetch users for drivers/conductors dropdown
      try {
        const usersRes = await axios.get(`${API_URL}/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const allUsers = usersRes.data || [];
        const foundDrivers = allUsers.filter(u => u.role === 'driver');
        const foundConductors = allUsers.filter(u => u.role === 'conductor');
        setDrivers(foundDrivers.length > 0 ? foundDrivers : DEFAULT_DRIVERS);
        setConductors(foundConductors.length > 0 ? foundConductors : DEFAULT_CONDUCTORS);
      } catch (e) {
        setDrivers(DEFAULT_DRIVERS);
        setConductors(DEFAULT_CONDUCTORS);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const resetForm = () => {
    setFormData({
      route_id: '',
      bus_id: '',
      driver_id: 'driver-takudzwa',
      driver_name: 'Takudzwa Kuwenyi (Driver)',
      driver_phone: '+263 777 275 154',
      conductor_id: 'conductor-chipo',
      conductor_name: 'Chipo Chiduku (Host)',
      conductor_phone: '+263 786 563 430',
      departure_datetime: '',
      arrival_datetime: '',
      price_usd: 0,
      price_zar: 0
    });
  };

  const handleCreateTrip = async () => {
    if (!formData.route_id || !formData.bus_id || !formData.departure_datetime) {
      toast.error('Please fill in Route, Bus, and Departure time');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/trips`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Trip created successfully with Driver & Host assigned!');
      setDialogOpen(false);
      fetchData();
      resetForm();
    } catch (error) {
      console.error('Failed to create trip:', error);
      toast.error(error.response?.data?.detail || 'Failed to create trip');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelTrip = async () => {
    if (!cancelModalTrip) return;
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/trips/${cancelModalTrip.id}/cancel`,
        { reason: cancelReason || 'Cancelled by authorized staff' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Trip ${cancelModalTrip.trip_number || ''} cancelled successfully`);
      setCancelModalTrip(null);
      setCancelReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel trip');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredTrips = trips.filter(trip =>
    trip.route_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trip.bus_registration?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trip.driver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trip.conductor_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-panashe-red" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-trips-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
              Manage Trips
            </h1>
            <p className="text-slate-600">
              Create and manage bus trips with Driver and Host crew
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-panashe" data-testid="create-trip-btn" onClick={resetForm}>
                <Plus className="h-4 w-4 mr-2" />
                Create Trip
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <Bus className="h-5 w-5 text-panashe-red" />
                  Create New Trip
                </DialogTitle>
              </DialogHeader>
              <TripFormFields 
                formData={formData}
                onChange={handleFieldChange}
                routes={routes}
                buses={buses}
                drivers={drivers}
                conductors={conductors}
              />
              <Button 
                className="w-full btn-panashe mt-4"
                onClick={handleCreateTrip}
                disabled={saving}
                data-testid="submit-trip-btn"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Create Trip &amp; Assign Crew
              </Button>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search trips by route, bus, driver, or host..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="trip-search-input"
          />
        </div>

        {/* Trips List */}
        <Card className="card-panashe">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              All Trips
              <Badge variant="outline">{filteredTrips.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Route</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Bus</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Trip Crew</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Departure</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Seats</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredTrips.map(trip => (
                    <tr key={trip.id} className="hover:bg-slate-50" data-testid={`trip-row-${trip.id}`}>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{trip.origin} → {trip.destination}</p>
                          <p className="text-sm text-slate-500">{trip.route_name}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-sm">
                        {trip.bus_registration}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div className="space-y-0.5">
                          <p className="text-slate-800 font-medium flex items-center gap-1">
                            <span className="text-slate-500">Driver:</span> {trip.driver_name || 'Takudzwa Kuwenyi'}
                          </p>
                          <p className="text-emerald-700 font-medium flex items-center gap-1">
                            <span className="text-slate-500">Host:</span> {trip.conductor_name || 'Chipo Chiduku'}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-slate-400" />
                          <span className="text-sm">
                            {format(parseISO(trip.departure_datetime), 'dd MMM')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-slate-400" />
                          <span className="text-sm">
                            {format(parseISO(trip.departure_datetime), 'HH:mm')}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium">${trip.price_usd}</p>
                        <p className="text-sm text-slate-500">R{trip.price_zar}</p>
                        {trip.expense_allowance > 0 && (
                          <div className="mt-1 text-[11px] bg-purple-50 text-purple-800 p-1 rounded border border-purple-200">
                            <p>Float: ${trip.expense_allowance.toFixed(0)}</p>
                            <p className="font-bold text-emerald-700">Rem: ${Number(trip.expense_balance !== undefined ? trip.expense_balance : trip.expense_allowance).toFixed(0)}</p>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4 text-slate-400" />
                          <span>{trip.total_seats - trip.available_seats}/{trip.total_seats}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={
                          trip.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                          trip.status === 'in_transit' ? 'bg-green-100 text-green-700' :
                          trip.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                          trip.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {trip.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-blue-700 hover:text-blue-800 hover:bg-blue-50"
                            title="View passenger manifest, including trip history"
                            aria-label={`View passenger manifest for ${trip.trip_number || trip.route_name}`}
                            onClick={() => navigate(`/dashboard/manifest/${trip.id}?history=1`)}
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                          {trip.status !== 'cancelled' && trip.status !== 'completed' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              title="Cancel Trip"
                              onClick={() => {
                                setCancelModalTrip(trip);
                                setCancelReason('');
                              }}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {filteredTrips.length === 0 && !loading && (
          <Card className="p-12 text-center">
            <MapPin className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">No Trips Found</h3>
            <p className="text-slate-600 mb-4">Create your first trip to get started</p>
            <Button className="btn-panashe" onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Trip
            </Button>
          </Card>
        )}

        {/* Cancel Trip Dialog */}
        <Dialog open={!!cancelModalTrip} onOpenChange={(open) => !open && setCancelModalTrip(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-5 w-5" /> Cancel Trip
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-sm text-slate-600">
                Are you sure you want to cancel trip <span className="font-semibold">{cancelModalTrip?.route_name}</span> ({cancelModalTrip?.trip_number})?
              </p>
              <div>
                <Label htmlFor="cancel-reason">Cancellation Reason *</Label>
                <Input
                  id="cancel-reason"
                  placeholder="e.g., Mechanical breakdown, operational rescheduling..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setCancelModalTrip(null)} disabled={actionLoading}>
                  Keep Trip
                </Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={handleCancelTrip}
                  disabled={actionLoading}
                >
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <XCircle className="h-4 w-4 mr-1" />}
                  Confirm Cancellation
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
};

export default AdminTrips;
